import { FC, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Modal } from '@/components/modal';
import { getPresignedDownloadUrl } from '@/services/data-sources/get-presigned-download-url';
import { isPreviewableMimeType } from '@/utils/preview-supported-mime-types';

interface FilePreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string | undefined;
  collectionId: string | undefined;
  filename: string;
  contentType: string | null | undefined;
}

export const FilePreviewModal: FC<FilePreviewModalProps> = ({
  open,
  onOpenChange,
  orgId,
  collectionId,
  filename,
  contentType,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const supported = isPreviewableMimeType(contentType);

  useEffect(() => {
    if (!open || !supported || !orgId || !collectionId || !filename) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setPreviewUrl(null);

    (async () => {
      try {
        const result = await getPresignedDownloadUrl({
          orgId,
          collectionId,
          filename,
          download: false,
        });

        if (cancelled) return;

        if (!result || 'detail' in result) {
          setError(
            (result as { detail?: string })?.detail ||
              'Failed to load preview.',
          );
          return;
        }

        setPreviewUrl(result.presigned_url);
      } catch (err) {
        if (cancelled) return;
        console.error('Preview error:', err);
        Sentry.captureException(err, {
          extra: {
            context: 'file-preview-modal',
            orgId,
            collectionId,
            filename,
          },
        });
        setError('Failed to load preview.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, supported, orgId, collectionId, filename]);

  useEffect(() => {
    if (!open) {
      setPreviewUrl(null);
      setError(null);
      setLoading(false);
    }
  }, [open]);

  return (
    <Modal
      isDialogOpen={open}
      setIsDialogOpen={onOpenChange}
      title={filename || 'Preview'}
      contentClassName='sm:max-w-4xl'
    >
      <div className='h-[75vh] w-full'>
        {!supported ? (
          <div className='flex h-full flex-col items-center justify-center gap-2 text-center'>
            <AlertCircle className='size-6 text-muted-foreground' />
            <p className='text-sm text-muted-foreground'>
              Preview is not available for this file type.
            </p>
          </div>
        ) : loading ? (
          <div className='flex h-full items-center justify-center'>
            <Loader2 className='size-6 animate-spin text-muted-foreground' />
          </div>
        ) : error ? (
          <div className='flex h-full flex-col items-center justify-center gap-2 text-center'>
            <AlertCircle className='size-6 text-destructive' />
            <p className='text-sm text-muted-foreground'>{error}</p>
          </div>
        ) : previewUrl ? (
          <object
            data={previewUrl}
            type={contentType || 'application/pdf'}
            className='h-full w-full rounded border'
            aria-label={`Preview of ${filename}`}
          >
            <div className='flex h-full flex-col items-center justify-center gap-2 text-center'>
              <AlertCircle className='size-6 text-muted-foreground' />
              <p className='text-sm text-muted-foreground'>
                Your browser can't display this preview inline.
              </p>
              <a
                href={previewUrl}
                target='_blank'
                rel='noreferrer'
                className='text-sm text-primary underline'
              >
                Open in new tab
              </a>
            </div>
          </object>
        ) : null}
      </div>
    </Modal>
  );
};
